// The spaced-repetition scheduler. Pure and dependency-free, so it can be unit-tested directly
// and run unchanged in the browser and on the server.
//
// This is a deliberately small SM-2 variant. A card starts in learning (short, minute-scale steps
// so a new word comes back within the same session), graduates to day-scale intervals, and then
// grows by its own ease factor. Forgetting a card drops it back into learning and permanently
// lowers its ease, so words you repeatedly miss keep coming back more often than ones you don't.

import type { CardState, Grade } from './types.js';

export const MINUTE = 60_000;
export const DAY = 1440 * MINUTE;

/** Ease bounds. The floor matters: without it, a card missed many times spirals to a zero interval. */
const MIN_EASE = 1.3;
const MAX_EASE = 3.0;
const START_EASE = 2.5;

/** A forgotten card comes back inside the session rather than tomorrow. */
const RELEARN_MIN = 10;
/** First graduating interval, and the "hard" step for a card still in learning. */
const FIRST_GOOD_MIN = 1440; // one day
const FIRST_HARD_MIN = 30;
const FIRST_EASY_MIN = 4 * 1440;

/** Nothing is scheduled beyond a year out; past that the spacing stops meaning anything. */
const MAX_INTERVAL_MIN = 365 * 1440;

/** How many of the easiest unseen cards a shuffled session draws from. */
export const SHUFFLE_WINDOW = 40;

const EASE_DELTA: Record<Grade, number> = {
  again: -0.2,
  hard: -0.15,
  good: 0,
  easy: 0.15,
};

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** The state a word starts in: due immediately, never seen. */
export function newCard(now = Date.now()): CardState {
  return { reps: 0, lapses: 0, ease: START_EASE, intervalMin: 0, dueAt: now };
}

/**
 * Apply a grade to a card and return its next state. Never mutates the input.
 *
 * `reps === 0` means the card is in learning — either brand new or just forgotten — and takes the
 * fixed steps above. A graduated card multiplies its current interval: `hard` grows it slowly
 * regardless of ease (so a struggling card still moves forward), `good` grows it by the ease
 * factor, and `easy` adds a further bonus on top.
 */
export function review(state: CardState, grade: Grade, now = Date.now()): CardState {
  const ease = clamp(state.ease + EASE_DELTA[grade], MIN_EASE, MAX_EASE);

  if (grade === 'again') {
    return {
      reps: 0,
      // Only a card that had actually graduated can be said to have lapsed; failing a card that
      // is still in learning is just part of learning it.
      lapses: state.lapses + (state.reps > 0 ? 1 : 0),
      ease,
      intervalMin: RELEARN_MIN,
      dueAt: now + RELEARN_MIN * MINUTE,
    };
  }

  let intervalMin: number;
  if (state.reps === 0) {
    intervalMin = grade === 'hard' ? FIRST_HARD_MIN : grade === 'easy' ? FIRST_EASY_MIN : FIRST_GOOD_MIN;
  } else {
    const factor = grade === 'hard' ? 1.2 : grade === 'easy' ? ease * 1.3 : ease;
    intervalMin = Math.round(state.intervalMin * factor);
  }
  intervalMin = clamp(intervalMin, 1, MAX_INTERVAL_MIN);

  return {
    // A "hard" answer on a learning card keeps it in learning: it has not been recalled cleanly yet.
    reps: state.reps === 0 && grade === 'hard' ? 0 : state.reps + 1,
    lapses: state.lapses,
    ease,
    intervalMin,
    dueAt: now + intervalMin * MINUTE,
  };
}

/** A card counts as learned once its spacing reaches a day — met is not the same as retained. */
export function isLearned(state: CardState): boolean {
  return state.intervalMin >= FIRST_GOOD_MIN;
}

export function isDue(state: CardState, now = Date.now()): boolean {
  return state.dueAt <= now;
}

/**
 * Choose the next card for a session.
 *
 * Due cards come first, oldest due first, so nothing waits longer than it has to. When nothing is
 * due, the deck's next unseen word is introduced — and because decks arrive in frequency order,
 * that is always the most common word not yet met. `introLimit` caps how many new words a session
 * will introduce, which is what keeps a 1000-word deck from dumping 1000 unknown cards on you.
 */
export function pickNext<T>(
  order: readonly T[],
  states: ReadonlyMap<T, CardState>,
  opts: {
    now?: number;
    introduced?: number;
    introLimit?: number;
    exclude?: ReadonlySet<T>;
    /** Draw the next NEW card at random instead of taking the deck in order. */
    shuffle?: boolean;
    /** Injectable so the shuffle can be tested deterministically. */
    random?: () => number;
    /**
     * When present, only these may be introduced as NEW cards. Due cards are unaffected: a card
     * that has fallen due is due whatever else is true of it, and filtering those would be the
     * scheduler quietly not running.
     */
    introducible?: ReadonlySet<T>;
  } = {},
): T | null {
  const now = opts.now ?? Date.now();
  const introduced = opts.introduced ?? 0;
  const introLimit = opts.introLimit ?? Infinity;
  const exclude = opts.exclude ?? new Set<T>();

  let bestDue: T | null = null;
  let bestDueAt = Infinity;
  for (const id of order) {
    if (exclude.has(id)) continue;
    const st = states.get(id);
    if (!st || !isDue(st, now)) continue;
    if (st.dueAt < bestDueAt) {
      bestDueAt = st.dueAt;
      bestDue = id;
    }
  }
  if (bestDue !== null) return bestDue;

  if (introduced < introLimit) {
    const eligible = (id: T) =>
      !states.has(id) && !exclude.has(id) && (!opts.introducible || opts.introducible.has(id));

    if (!opts.shuffle) {
      for (const id of order) if (eligible(id)) return id;
      return null;
    }
    const unseen: T[] = [];
    for (const id of order) {
      if (!eligible(id)) continue;
      unseen.push(id);
      if (unseen.length === SHUFFLE_WINDOW) break;
    }
    if (!unseen.length) return null;
    const pick = Math.floor((opts.random ?? Math.random)() * unseen.length);
    // Guard the edge: a random() returning exactly 1 would index past the end.
    return unseen[Math.min(pick, unseen.length - 1)]!;
  }
  return null;
}
